import React, { useState, useCallback, useEffect } from 'react';
import { MessengerWindow } from './MessengerWindow';

interface MessengerWindowState {
  id: string;
  conversationId?: string | null;
  userId?: string | null;
  position: { x: number; y: number };
  isMinimized: boolean;
  minimizedIndex: number;
  zIndex: number;
}

let windowIdCounter = 0;
let zIndexCounter = 1000;
const windows: Map<string, MessengerWindowState> = new Map();
const listeners: Set<(windows: MessengerWindowState[]) => void> = new Set();

const notifyListeners = () => {
  const windowArray = Array.from(windows.values());
  listeners.forEach(listener => listener(windowArray));
};

export const openMessenger = (userId?: string | null, conversationId?: string | null) => {
  const windowId = `messenger-${++windowIdCounter}`;
  const position = {
    x: 100 + (windows.size * 30),
    y: 100 + (windows.size * 30)
  };

  const windowState: MessengerWindowState = {
    id: windowId,
    conversationId: conversationId || null,
    userId: userId || null,
    position,
    isMinimized: false,
    minimizedIndex: 0,
    zIndex: zIndexCounter++
  };

  windows.set(windowId, windowState);
  notifyListeners();
};

export const MessengerManager: React.FC = () => {
  const [windowArray, setWindowArray] = useState<MessengerWindowState[]>([]);

  const updateWindows = useCallback((windows: MessengerWindowState[]) => {
    setWindowArray([...windows]);
  }, []);

  useEffect(() => {
    listeners.add(updateWindows);
    // Initialize with current windows
    updateWindows(Array.from(windows.values()));
    return () => {
      listeners.delete(updateWindows);
    };
  }, [updateWindows]);

  const handleClose = useCallback((windowId: string) => {
    windows.delete(windowId);
    notifyListeners();
  }, []);

  const handleMinimize = useCallback((windowId: string) => {
    const window = windows.get(windowId);
    if (window) {
      window.isMinimized = !window.isMinimized;
      if (window.isMinimized) {
        // Calculate minimized index
        const minimizedWindows = Array.from(windows.values())
          .filter(w => w.isMinimized)
          .sort((a, b) => (a.minimizedIndex || 0) - (b.minimizedIndex || 0));
        window.minimizedIndex = minimizedWindows.length;
      } else {
        window.minimizedIndex = 0;
      }
      notifyListeners();
    }
  }, []);

  const handleFocus = useCallback((windowId: string) => {
    const window = windows.get(windowId);
    if (window) {
      window.zIndex = zIndexCounter++;
      notifyListeners();
    }
  }, []);

  return (
    <>
      {windowArray.map((window) => (
        <MessengerWindow
          key={window.id}
          windowId={window.id}
          initialConversationId={window.conversationId}
          initialUserId={window.userId}
          position={window.position}
          onClose={handleClose}
          onMinimize={handleMinimize}
          onFocus={handleFocus}
          isMinimized={window.isMinimized}
          minimizedIndex={window.minimizedIndex}
          zIndex={window.zIndex}
        />
      ))}
    </>
  );
};
